/**
 * Payement.jsx — Page de paiement Stripe
 *
 * Flux :
 *  1. Charge la clé publique Stripe depuis /api/payments/config
 *  2. Crée un PaymentIntent via /api/payments/create-intent
 *  3. Affiche le formulaire carte (CardElement de Stripe)
 *  4. Confirme le paiement côté Stripe (stripe.confirmCardPayment)
 *  5. Enregistre en base via /api/payments/confirm
 *  6. Redirige vers /payment-success avec les données de la réservation
 *
 * Carte de test : 4242 4242 4242 4242 — exp: 12/26 — CVC: 123
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FiArrowLeft, FiCreditCard, FiLock, FiShield } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import BottomNav from '../shared/BottomNav.jsx';
import '../home/Home.css';
import './Payement.css';

const API_BASE = getApiBaseUrl();

function formatCurrency(value) {
	return `${Number(value || 0).toLocaleString('fr-FR')} DH`;
}

function computeRentalDays(startDate, endDate) {
	if (!startDate || !endDate) return 1;
	const diff = Math.ceil((new Date(endDate) - new Date(startDate)) / 86400000);
	return Math.max(1, diff || 1);
}

/** Style du CardElement Stripe pour s'harmoniser avec le thème sombre */
const CARD_ELEMENT_OPTIONS = {
	style: {
		base: {
			color: '#e8f0ff',
			fontFamily: '"Inter", "Segoe UI", sans-serif',
			fontSize: '16px',
			fontSmoothing: 'antialiased',
			'::placeholder': { color: '#5a6a8a' },
			iconColor: '#4d9fff',
		},
		invalid: { color: '#ff6b6b', iconColor: '#ff6b6b' },
	},
	hidePostalCode: true,
};

/**
 * StripePayForm — Formulaire interne (doit être enfant de <Elements>).
 * Reçoit le clientSecret pour confirmer le paiement sans l'exposer dans l'URL.
 */
// fetch avec timeout — évite de bloquer indéfiniment si le backend est lent (Render cold start)
function fetchWithTimeout(url, options = {}, timeoutMs = 40000) {
	const controller = new AbortController();
	const id = setTimeout(() => controller.abort(), timeoutMs);
	return fetch(url, { ...options, signal: controller.signal })
		.finally(() => clearTimeout(id));
}

function StripePayForm({ booking, summary, clientSecret, paymentIntentId, onSuccess }) {
	const stripe = useStripe();
	const elements = useElements();
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [step, setStep] = useState(''); // message d'étape affiché pendant le traitement

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!stripe || !elements) return;
		setSubmitting(true);
		setError('');
		setStep('Vérification du paiement…');

		const card = elements.getElement(CardElement);

		try {
			// 1. Confirme le paiement côté Stripe
			const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(clientSecret, {
				payment_method: { card },
				return_url: `${window.location.origin}/payment-success`,
			});

			if (stripeError) {
				setError(stripeError.message || 'Erreur de paiement.');
				return;
			}

			if (paymentIntent?.status !== 'succeeded') {
				setError(`Paiement non finalisé (statut: ${paymentIntent?.status}).`);
				return;
			}

			// 2. Enregistre en base via le backend
			setStep('Enregistrement de la réservation…');
			const res = await fetchWithTimeout(`${API_BASE}/api/payments/confirm`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					paymentIntentId: paymentIntent.id,
					reservationId: booking.reservationId || null,
					userId: booking.customerId || booking.userId || null,
					vehicleName: booking.vehicleName || 'Véhicule',
					agencyName: booking.agencyName || 'Agence partenaire',
					customerName: booking.customerName || '',
					customerEmail: booking.customerEmail || '',
					customerPhone: booking.customerPhone || '',
					amount: summary.total,
					currency: 'MAD',
					vehiclePhotoUrl: booking.vehiclePhotoUrl || null,
					licenseUrls: booking.licenseUrls || [],
					meta: {
						startDate: booking.startDate || null,
						endDate: booking.endDate || null,
						pickupLocation: booking.pickupLocation || booking.ville || null,
						options: booking.options || {},
						summary,
					},
				}),
			}, 40000);

			const data = await res.json().catch(() => ({}));
			if (!res.ok) throw new Error(data?.message || 'Erreur enregistrement.');

			// 3. Message automatique à l'agence (non bloquant)
			setStep('Finalisation…');
			try {
				const period = (booking.startDate && booking.endDate)
					? `du ${booking.startDate} au ${booking.endDate}` : '';
				const pickup = booking.pickupLocation || booking.ville || '';
				const msgText = [
					`✅ Paiement confirmé — Nouvelle réservation reçue.`,
					`Véhicule : ${booking.vehicleName || 'Véhicule'}`,
					period && `Période : ${period}`,
					pickup && `Lieu de récupération : ${pickup}`,
					`Référence : ${data.reference || '—'}`,
					`Montant payé : ${summary.total} DH`,
				].filter(Boolean).join('\n');

				const discRes = await fetchWithTimeout(`${API_BASE}/api/discussions`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						contextType: 'agency',
						listingId: booking.listingId || '',
						agencyId: booking.agencyId || null,
						agencyName: booking.agencyName || '',
						customerId: booking.customerId || null,
						customerName: booking.customerName || 'Client',
						customerEmail: booking.customerEmail || '',
						vehicleName: booking.vehicleName || '',
					}),
				}, 15000);
				const disc = await discRes.json().catch(() => null);
				if (disc?._id) {
					await fetchWithTimeout(`${API_BASE}/api/discussions/${disc._id}/messages`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							text: msgText,
							senderRole: 'customer',
							senderName: booking.customerName || 'Client',
							senderId: booking.customerId || null,
						}),
					}, 15000).catch(() => {});
				}
			} catch (_) { /* non bloquant */ }

			onSuccess({ reference: data.reference, payment: data, booking, summary });
		} catch (err) {
			if (err.name === 'AbortError') {
				setError('Le serveur met trop de temps à répondre. Votre paiement a peut-être été traité — vérifiez votre email ou contactez le support.');
			} else {
				setError(err.message || 'Une erreur est survenue. Réessayez.');
			}
		} finally {
			setSubmitting(false);
			setStep('');
		}
	};

	return (
		<form className="vx-pay-form" onSubmit={handleSubmit}>
			<div className="vx-pay-secure-notice">
				<FiLock size={14} />
				<span>Paiement sécurisé — toutes les transactions sont chiffrées par Stripe</span>
			</div>

			<div className="vx-pay-amount-display">
				<span className="vx-pay-amount-label">Montant total</span>
				<span className="vx-pay-amount-value">{formatCurrency(summary.total)}</span>
			</div>

			{/* Champ carte Stripe */}
			<div className="vx-pay-card-field">
				<label className="vx-pay-card-label">
					<FiCreditCard size={15} />
					Carte bancaire
				</label>
				<div className="vx-pay-card-element-wrap">
					<CardElement options={CARD_ELEMENT_OPTIONS} />
				</div>
				{/* Carte de test visible uniquement en développement */}
				<p className="vx-pay-test-hint">
					Test : <strong>4242 4242 4242 4242</strong> · Exp: <strong>12/26</strong> · CVC: <strong>123</strong>
				</p>
			</div>

			{error && <p className="vx-pay-error">{error}</p>}

			<button type="submit" className="vx-pay-submit" disabled={submitting || !stripe}>
				{submitting ? (step || 'Traitement en cours…') : `Payer ${formatCurrency(summary.total)}`}
			</button>

			<p className="vx-pay-note">
				<FiShield size={13} /> Transaction sécurisée SSL · Confirmation immédiate par email
			</p>
		</form>
	);
}

function Payement() {
	const navigate = useNavigate();
	const location = useLocation();
	const locationStateRef = useRef(location.state);
	const booking = useMemo(() => locationStateRef.current || {}, []);

	const [stripePromise, setStripePromise] = useState(null);
	const [clientSecret, setClientSecret] = useState('');
	const [paymentIntentId, setPaymentIntentId] = useState('');
	const [initError, setInitError] = useState('');

	const summary = useMemo(() => {
		const raw = booking.priceSummary || {};
		const options = Array.isArray(booking.options) ? booking.options : [];
		const rentalDays = Number(raw.rentalDays) || computeRentalDays(booking.startDate, booking.endDate);
		const dailyRate = Number(raw.dailyRate ?? booking.prixJour ?? 0);
		const baseTotal = Number(raw.baseTotal ?? dailyRate * rentalDays);
		const optionsTotal = Number(
			raw.optionsTotal ?? raw.optionsPrice ??
			options.reduce((t, o) => t + Number(o?.price || o?.prix || 0), 0)
		);
		const total = Number(booking.totalPrice ?? raw.total ?? baseTotal + optionsTotal);
		return { rentalDays, baseTotal, optionsTotal, total };
	}, [booking]);

	// Charge la clé publique Stripe + crée le PaymentIntent au montage
	useEffect(() => {
		if (!summary.total) return;

		const init = async () => {
			try {
				// 1. Récupère la clé publique Stripe
				const configRes = await fetch(`${API_BASE}/api/payments/config`);
				const config = await configRes.json();
				if (!config.publishableKey) throw new Error('Clé Stripe non configurée.');
				setStripePromise(loadStripe(config.publishableKey));

				// 2. Crée le PaymentIntent côté backend
				const intentRes = await fetch(`${API_BASE}/api/payments/create-intent`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						amount: summary.total,
						currency: 'mad',
						vehicleName: booking.vehicleName || 'Véhicule',
						agencyName: booking.agencyName || 'Agence partenaire',
						customerEmail: booking.customerEmail || '',
					}),
				});
				const intentData = await intentRes.json();
				if (!intentRes.ok) throw new Error(intentData?.message || 'Impossible de créer le paiement.');
				setClientSecret(intentData.clientSecret);
				setPaymentIntentId(intentData.paymentIntentId);
			} catch (err) {
				setInitError(err.message || 'Initialisation Stripe impossible.');
			}
		};

		init();
	}, [summary.total, booking.vehicleName, booking.agencyName, booking.customerEmail]);

	const handleSuccess = useCallback((payment) => {
		navigate('/payment-success', { state: payment });
	}, [navigate]);

	return (
		<div className="vx-pay-page">
			<header className="vx-pay-nav-wrap">
				<nav className="vx-pay-nav">
					<Link to="/" className="vx-pay-brand">
						<div className="vx-pay-brand-mark">
							<img src="/assets/images/logo.png" alt="" aria-hidden="true" />
						</div>
						<span className="vx-pay-brand-name">UltimateXDrive</span>
					</Link>
					<div />
					<button type="button" className="vx-pay-nav-back" onClick={() => navigate(-1)}>
						<FiArrowLeft size={15} /> Retour
					</button>
				</nav>
			</header>

			<main className="vx-pay-main">
				<div className="vx-pay-wrap">
					<header className="vx-pay-header">
						<div className="vx-pay-eyebrow">
							<span className="vx-pay-eyebrow-dot" />
							Paiement sécurisé
						</div>
						<h1 className="vx-pay-title">
							Finalisez<br /><em>votre réservation.</em>
						</h1>
						<p className="vx-pay-subtitle">
							Validation instantanée, transaction chiffrée et confirmation immédiate.
						</p>
					</header>

					<section className="vx-pay-layout">
						{/* Formulaire de paiement */}
						<article className="vx-pay-card">
							{initError ? (
								<div className="vx-pay-init-error">
									<p>{initError}</p>
									<button type="button" className="vx-pay-back-home" onClick={() => navigate(-1)}>
										Revenir en arrière
									</button>
								</div>
							) : !stripePromise || !clientSecret ? (
								<div className="vx-pay-loading">
									<div className="vx-pay-spinner" />
									<p>Initialisation du paiement sécurisé…</p>
								</div>
							) : (
								<Elements stripe={stripePromise} options={{ clientSecret }}>
									<StripePayForm
										booking={booking}
										summary={summary}
										clientSecret={clientSecret}
										paymentIntentId={paymentIntentId}
										onSuccess={handleSuccess}
									/>
								</Elements>
							)}
						</article>

						{/* Récapitulatif de la réservation */}
						<aside className="vx-pay-summary">
							<h2>Récapitulatif</h2>
							<div className="vx-pay-summary-block">
								<p>Véhicule</p>
								<strong>{booking.vehicleName || 'Véhicule'}</strong>
							</div>
							<div className="vx-pay-summary-block">
								<p>Agence</p>
								<strong>{booking.agencyName || 'Agence partenaire'}</strong>
							</div>
							<div className="vx-pay-summary-meta">
								<span>Période</span>
								<strong>{booking.startDate || '--'} → {booking.endDate || '--'}</strong>
							</div>
							<div className="vx-pay-summary-meta">
								<span>Lieu de récupération</span>
								<strong>{booking.pickupLocation || booking.ville || 'À définir'}</strong>
							</div>
							<hr className="vx-pay-divider" />
							<div className="vx-pay-summary-line">
								<span>Location ({summary.rentalDays} j.)</span>
								<strong>{formatCurrency(summary.baseTotal)}</strong>
							</div>
							{summary.optionsTotal > 0 && (
								<div className="vx-pay-summary-line">
									<span>Options</span>
									<strong>{formatCurrency(summary.optionsTotal)}</strong>
								</div>
							)}
							<div className="vx-pay-summary-total">
								<span>Total à payer</span>
								<strong>{formatCurrency(summary.total)}</strong>
							</div>
							<p className="vx-pay-note"><FiShield size={13} /> Paiement 100 % sécurisé</p>
						</aside>
					</section>
				</div>
			</main>

			<BottomNav />
		</div>
	);
}

export default Payement;
